import { IF, NO, UIColor, UIRoute, UITextView, UIView, UIViewController, YES } from "uicore-ts"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView } from "../Custom components/RH/RHDataView"
import { formatRHDate } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHTables } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"

import { RHProcurementPlanViewTokens, RHResponsiveTextTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { labelForRHViewport } from "../Custom components/RH/Utils_slop/RHResponsiveText"


interface ProcurementPlanEntry {
    _id?: string
    title?: string
    estimatedValue?: number
    currency?: string
    plannedPublishDate?: Date
    cpvCode?: string
    status?: string
}


export class ProcurementPlanViewController extends UIViewController {


    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header2
    ).configuredWithObject({
        text: "Hankeplaan",
        backgroundColor: UIColor.transparentColor
    }).addedAsSubviewToView(this.view)

    readonly planDataView = new RHDataView<ProcurementPlanEntry>().configuredWithObject({
        descriptors: [
            {
                title: "Nimetus",
                keyPath: "title",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.titleLabel!.text = IF(dataItem.title)(() =>
                        RHTextSanitizer.escapedHTML(dataItem.title!)
                    ).ELSE(() => "-")
                }
            },
            {
                title: "CPV-kood",
                keyPath: "cpvCode",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.titleLabel!.text = dataItem.cpvCode || "-"
                }
            },
            {
                title: labelForRHViewport(RHResponsiveTextTokens.tableHeaderLabels.estimatedCost),
                keyPath: "estimatedValue",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    if (dataItem.estimatedValue != null) {
                        cellView.titleLabel!.text = RHEuroFormatter.formattedAmount(dataItem.estimatedValue)
                    }
                    else {
                        cellView.titleLabel!.text = "-"
                    }
                }
            },
            {
                title: "Kavandatav avaldamise aeg",
                keyPath: "plannedPublishDate",
                allowSorting: YES,
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    if (dataItem.plannedPublishDate) {
                        const date = new Date(dataItem.plannedPublishDate)
                        cellView.titleLabel!.text = formatRHDate(date)
                    }
                    else {
                        cellView.titleLabel!.text = "-"
                    }
                }
            },
            {
                title: "Staatus",
                keyPath: "status",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    cellView.titleLabel!.text = dataItem.status || "-"
                }
            }
        ]
    }).addedAsSubviewToView(this.view)

    readonly bottomSpacerView = new UIView().addedAsSubviewToView(this.view)


    constructor(view: UIView) {
        super(view)

        this.view.configureWithObject({
            backgroundColor: RHColor.background
        })

        this._applyTableColumnLayout()
    }

    private _applyTableColumnLayout() {
        this.planDataView.tableHeaderView.configureWithObject({
            cellWeights: RHProcurementPlanViewTokens.columnWeights,
            intrinsicContentHeight: () => 64
        })
        this.planDataView.tableView.configureWithObject({
            cellWeights: RHProcurementPlanViewTokens.columnWeights
        })
        this._configureTableHeaderCells()
        this.planDataView.tableHeaderView.setNeedsLayout()
        this.planDataView.tableView.setNeedsLayout()
    }

    private _configureTableHeaderCells() {
        for (const headerCell of this.planDataView.tableHeaderView.cells) {
            const buttonView = headerCell.buttonView
            if (buttonView) {
                buttonView.contentPadding = 2
            }
            const titleLabel = buttonView?.titleLabel ?? headerCell.titleLabel
            if (!titleLabel) {
                continue
            }
            titleLabel.isSingleLine = NO
            titleLabel.style.whiteSpace = "normal"
            titleLabel.style.lineHeight = RHProcurementPlanViewTokens.multilineHeaderLineHeight
            titleLabel.textElementView.style.whiteSpace = "normal"
            titleLabel.textElementView.style.lineHeight = RHProcurementPlanViewTokens.multilineHeaderLineHeight
        }
    }


    static override readonly routeComponentName = "procurement_plan"
    static override readonly ParameterIdentifierName = {}


    override async viewDidAppear() {
        await super.viewDidAppear()

        this.planDataView.data = []
        this.planDataView.noDataMessage = "Hankeplaani kirjeid ei leitud."

        this.view.setNeedsLayout()
    }


    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)
    }


    override layoutViewSubviews() {
        super.layoutViewSubviews()

        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)

        bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(padding, RHTables.dataViewPageHeight)
            .assignedAsFrameOfView(this.planDataView)
            .rectangleForNextRow(padding, this.bottomSpacerView)
            .assignedAsFrameOfView(this.bottomSpacerView)
    }

}
